import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import useAuthStore from '../store/auth-store'
import { UserDropdown } from './UserDropdown'
import { MobileNavBar } from './MobileNavBar'
import { SignInModal } from './SignInModal'
import { CreateAccountModal } from './CreateAccountModal'
import { getUnreadCount, getNotificationPrefs } from '../lib/messageApi'

// Reads the project id the dashboard last persisted (DashboardPage writes
// it into localStorage on mount). Falls back to null when there's no
// active project; the unread-count poll is skipped in that case.
const lastProjectId = () => {
  try {
    const v = localStorage.getItem('wq_last_project_id')
    return v ? Number(v) : null
  } catch { return null }
}

// Poll cadence for unread messages. 5s while the tab is visible so the
// badge shows new messages within a few seconds of another user posting;
// 30s while the tab is hidden so we don't burn API requests on
// background tabs. The poll is also re-armed immediately via the
// `wq_messages_changed` window event from MessageCenterPage so locally-
// triggered changes refresh the badge without waiting for the next tick.
const UNREAD_POLL_VISIBLE_MS = 5_000
const UNREAD_POLL_HIDDEN_MS  = 30_000

// Best-effort browser notification. Asks for permission on the first
// growth event (browsers reject silent permission prompts; this means the
// FIRST batch of new messages might not pop a notification — that's the
// trade-off). Subsequent growth events with granted permission pop a
// system notification that links back to /messages on click.
function tryBrowserNotify(count) {
  if (typeof window === 'undefined' || !('Notification' in window)) return
  const fire = () => {
    try {
      const n = new Notification('New messages in WQData LIVE', {
        body: `${count} unread message${count === 1 ? '' : 's'} in Message Center`,
        // Reusing the favicon avoids needing a new asset.
        icon: '/favicon.ico',
        tag:  'wq-msg-unread', // collapses duplicates
      })
      n.onclick = () => { window.focus(); window.location.href = '/messages' }
    } catch { /* some browsers throw on insecure contexts */ }
  }
  if (Notification.permission === 'granted') { fire(); return }
  if (Notification.permission !== 'denied') {
    Notification.requestPermission().then((p) => { if (p === 'granted') fire() }).catch(() => {})
  }
}

/**
 * Site-wide navigation. Pass `hideMarketingBar` to suppress the
 * white row (logo + About/Features/Demo/Plans + Get Started) —
 * used on the Public Portal, where the project's own header stands
 * in for the marketing chrome.
 */
export function Navigation({ hideMarketingBar = false } = {}) {
  const [showSignInModal, setShowSignInModal] = useState(false)
  const [showCreateAccountModal, setShowCreateAccountModal] = useState(false)
  const { isAuthenticated, user, logout } = useAuthStore()

  // ── Active project name (XD: "Project: <name>" on the left of the blue bar) ──
  // DashboardPage mirrors the current project's name into localStorage and fires
  // a `wq_project_changed` event so this shared bar updates when switching
  // projects; `storage` covers cross-tab changes.
  const [projectName, setProjectName] = useState(() => {
    try { return localStorage.getItem('wq_last_project_name') || '' } catch { return '' }
  })
  useEffect(() => {
    const read = () => { try { setProjectName(localStorage.getItem('wq_last_project_name') || '') } catch {} }
    const onChanged = (e) => setProjectName(e?.detail?.name ?? '')
    window.addEventListener('wq_project_changed', onChanged)
    window.addEventListener('storage', read)
    return () => {
      window.removeEventListener('wq_project_changed', onChanged)
      window.removeEventListener('storage', read)
    }
  }, [])

  // ── Unread-message badge + browser notifications ──────────────────────
  // Polls the last-viewed-project's unread count every UNREAD_POLL_MS.
  // When the count GROWS while the user isn't currently on /messages and
  // their notification preference isn't muted, we fire a browser
  // notification too (after first asking for Notification permission).
  const [unread, setUnread] = useState(0)
  const lastSeenUnreadRef = useRef(0)
  const originalTitleRef  = useRef(typeof document !== 'undefined' ? document.title : 'WQData LIVE')
  useEffect(() => {
    if (!isAuthenticated) return
    let cancelled = false
    let timeoutId = null
    // Re-arming timeout (instead of a fixed setInterval) so visibility
    // changes can swap the cadence between visible (fast) and hidden
    // (slow) without leaving a stale interval running.
    const schedule = () => {
      if (cancelled) return
      const ms = document.visibilityState === 'visible'
        ? UNREAD_POLL_VISIBLE_MS
        : UNREAD_POLL_HIDDEN_MS
      timeoutId = setTimeout(tick, ms)
    }
    const tick = async () => {
      const pid = lastProjectId()
      if (!pid) { schedule(); return }
      try {
        const [u, p] = await Promise.all([
          getUnreadCount(pid).catch(() => ({ count: 0 })),
          getNotificationPrefs().catch(() => ({ is_muted: false, scope: 'all' })),
        ])
        if (cancelled) return
        const muted = !!p.is_muted
        const next  = Number(u.count || 0)
        setUnread(next)
        // Notify only on growth, and only when the user isn't already
        // looking at the Message Center (no point pinging there).
        const grew = next > lastSeenUnreadRef.current
        const onMessages = window.location.pathname.startsWith('/messages')
        if (grew && !muted && !onMessages) {
          tryBrowserNotify(next)
        }
        lastSeenUnreadRef.current = next
      } catch { /* swallow — next tick will try again */ }
      finally { schedule() }
    }
    // Immediate first tick so the badge appears as soon as the user lands.
    tick()
    // Local nudges from MessageCenterPage so the badge refreshes the
    // instant the user marks-read / deletes / sends locally.
    const onLocal = () => { clearTimeout(timeoutId); tick() }
    window.addEventListener('wq_messages_changed', onLocal)
    // Tab visibility changes: when the user flips BACK to this tab, force
    // an immediate poll instead of waiting out the slow hidden-cadence
    // timer. Lets the count refresh the moment the user re-engages.
    const onVis = () => {
      if (document.visibilityState === 'visible') { clearTimeout(timeoutId); tick() }
    }
    document.addEventListener('visibilitychange', onVis)
    return () => {
      cancelled = true
      clearTimeout(timeoutId)
      window.removeEventListener('wq_messages_changed', onLocal)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [isAuthenticated])

  // Mirror the unread count into the browser tab title so the user sees
  // new messages even when this tab is in the background. Example:
  // "(3) WQData LIVE". Cleared back to the original title at 0.
  useEffect(() => {
    if (typeof document === 'undefined') return
    const base = originalTitleRef.current.replace(/^\(\d+\)\s*/, '')
    document.title = unread > 0 ? `(${unread > 99 ? '99+' : unread}) ${base}` : base
  }, [unread])

  const handleSwitchToCreateAccount = () => {
    setShowSignInModal(false)
    setShowCreateAccountModal(true)
  }

  const handleSwitchToSignIn = () => {
    setShowCreateAccountModal(false)
    setShowSignInModal(true)
  }

  const handleLogout = () => {
    logout()
    window.location.href = '/login'
  }

  // Below 768px the XD replaces the stacked blue bar + logo row with a single
  // white row (MobileNavBar). Only do that where the marketing chrome exists:
  // the Public Portal passes hideMarketingBar and relies on the blue bar for
  // Sign In / Help Center, so it keeps the current header at every width.
  const useMobileBar = !hideMarketingBar

  return (
    <>
      <nav className="sticky top-0 z-[9999] respons-head">
        {useMobileBar && (
          <MobileNavBar
            isAuthenticated={isAuthenticated}
            unread={unread}
            onSignInClick={() => setShowSignInModal(true)}
            accountMenu={isAuthenticated ? <UserDropdown variant="mobile" /> : null}
          />
        )}

        {/* Top Dark Teal Bar — matches XD design. Hidden below 768px when the
            mobile row above stands in for it. */}
        <div className={`bg-[#358ECD] ${useMobileBar ? 'hidden md:block' : ''}`}>
          <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[26px] flex justify-between items-center gap-0">
            {/* Left: active project name (XD design) */}
            <div className="flex items-center min-w-0">
              {isAuthenticated && projectName && (
                <span className="text-white text-[15px] py-2.5 truncate max-w-[50vw]" title={projectName}>
                  <span className="font-semibold">Project:</span> {projectName}
                </span>
              )}
            </div>

            {/* Right: account + messages + help + nexsens */}
            <div className="flex items-center gap-0 self-stretch">
            {isAuthenticated ? (
              <UserDropdown />
            ) : (
              <button onClick={() => setShowSignInModal(true)} className="text-white text-[15px] hover:text-blue-100 transition cursor-pointer bg-transparent border-none px-5 py-2.5">Sign In</button>
            )}

            {/* Messages — only for authenticated users. Red unread-count
                badge attaches when the user has un-marked-read messages
                in their last-viewed project. */}
            {isAuthenticated && (
              <a href="/messages" className="relative flex items-center gap-1.5 px-4 py-2.5 text-white text-[15px] hover:bg-white/10 transition">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path d="M15 17H20L18.595 15.595C18.21 15.21 18 14.689 18 14.144V11C18 8.387 16.286 6.168 14 5.341V5C14 3.895 13.105 3 12 3C10.895 3 10 3.895 10 5V5.341C7.714 6.168 6 8.387 6 11V14.144C6 14.689 5.79 15.21 5.405 15.595L4 17H9M15 17V18C15 19.657 13.657 21 12 21C10.343 21 9 19.657 9 18V17M15 17H9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Messages</span>
                {unread > 0 && (
                  <span
                    title={`${unread} unread message${unread === 1 ? '' : 's'}`}
                    className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold leading-none"
                  >
                    {unread > 99 ? '99+' : unread}
                  </span>
                )}
              </a>
            )}

            {/* Help Center */}
            <a href="/help" className="flex items-center gap-1.5 px-5 py-2.5 text-white text-[15px] hover:bg-white/10 transition">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="white" strokeWidth="1.5"/>
                <path d="M12 17V16.5M12 13.5C12 13.5 14 12 14 10C14 8.895 13.105 8 12 8C10.895 8 10 8.895 10 10" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span>Help Center</span>
            </a>

            {/* nexsens.com */}
            <a
              href="https://www.nexsens.com"
              target="_blank"
              rel="noopener noreferrer"
              className="nexsens-btn text-sm px-6 py-2.5 bg-white text-[#4D4D4D] hover:bg-gray-100 transition self-stretch flex items-center border-l border-white/20"
            >
              nexsens.com
            </a>
            </div>
          </div>
        </div>
      
      {/* Main Navigation */}
      {!hideMarketingBar && (
      <div className="bg-white border-b border-gray-200 hidden md:block">
        <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[140px]">
           {!isAuthenticated && (
          <div className="head-pd flex justify-between items-end gap-2 py-2">
            {/* Logo */}
            <Link to="/" className="max-w-[25%] flex-[25%]">
              <img 
                src="/images/logo.png" 
                alt="WQData LIVE" 
                className="max-w-[100%]"
              />
            </Link>

            {/* Center Menu */}
            <div className="hidden md:flex items-end gap-[20px] max-w-[75%] flex-[75%] justify-end lg:gap-[50px] lg:max-w-[65%] lg:flex-[65%]">
              <a href="#about" className="text-[13px] lg:text-[15px] font-bold text-[#4D4D4D] hover:text-[#358ECD] transition">About WQData LIVE</a>
              <a href="#features" className="text-[13px] lg:text-[15px] font-bold text-[#4D4D4D] hover:text-[#358ECD] transition">Features</a>
              <a href="#projects" className="text-[13px] lg:text-[15px] font-bold text-[#4D4D4D] hover:text-[#358ECD] transition">Demo</a>
              <a href="#plans" className="text-[13px] lg:text-[15px] font-bold text-[#4D4D4D] hover:text-[#358ECD] transition">Plans</a>
              {/* Right side - Get Started Only */}
              <div className="flex items-end gap-4">
               
                  <a href="/login" className="text-[13px] lg:text-[15px] font-bold transition text-[#358ECD]">
                    Get Started
                  </a>
              </div>
            </div>

            
          </div>
          )}
        </div>
      </div>
      )}
    </nav>

    <SignInModal 
      isOpen={showSignInModal} 
      onClose={() => setShowSignInModal(false)}
      onSwitchToCreateAccount={handleSwitchToCreateAccount}
    />
    <CreateAccountModal 
      isOpen={showCreateAccountModal}
      onClose={() => setShowCreateAccountModal(false)}
      onSwitchToSignIn={handleSwitchToSignIn}
    />
    </>
  )
}
